import { observer } from 'mobx-react-lite'
import { Outlet, useLocation, useParams } from 'react-router'
import { useEffect, useMemo } from 'react'
import { layoutStore, SpinnerLoader } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { stringToNumber } from 'shared/lib/stringToNumber'
import { BreakpointsEnum } from 'shared/constants/breakpoints'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { contactsDetailsGlobalStore } from 'widgets/ContactsDetails/store'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'
import { ProfileLayoutPageStore } from 'pages/conversations/pages/search/pages/profile/store/profileLayoutPageStore'
import {
  ProviderProfileLayoutPage,
  useProfileLayoutPageContext,
} from 'pages/conversations/pages/search/pages/profile/context/profileLayoutContext'

const ProfileLayoutContent = observer(() => {
  const { search } = useLocation()
  const params = useParams()
  const store = useProfileLayoutPageContext()
  const query = new URLSearchParams(search)
  const contactId = stringToNumber(query.get('contactId'))
  const messageId = stringToNumber(query.get('messageId'))
  const conversationId = stringToNumber(params.conversationId)
  const readyIsLoad = useMemo(() => store.readyIsLoad, [store.readyIsLoad])
  const readyIsNext = useMemo(() => store.readyIsNext, [store.readyIsNext])
  const { activeBreakpoint, isLargeView, isMobileViewWithoutMSize } = layoutStore

  const backToSearch = () => {
    query.delete('contactId')
    query.delete('messageId')

    uiStore.changeRoute({
      path: `/${ConversationsPath.search}`,
      query: query.toString(),
    })
  }

  useEffect(() => {
    if (isLargeView) {
      contactsDetailsGlobalStore.handleChangeCollapse(false)

      return
    }

    if (activeBreakpoint === BreakpointsEnum.M) {
      contactsDetailsGlobalStore.handleChangeCollapse(false)

      return
    }

    if (isMobileViewWithoutMSize) {
      contactsDetailsGlobalStore.handleChangeCollapse(true)

      return
    }
  }, [activeBreakpoint, isLargeView, isMobileViewWithoutMSize])

  useEffect(() => {
    return () => {
      store.dispose()
    }
  }, [])

  useEffect(() => {
    store.setMessageId(messageId)
    store.setConversationId(conversationId)

    if (contactId) {
      store.setContactId(contactId)
    } else {
      backToSearch()
    }
  }, [messageId, conversationId, contactId])

  useEffect(() => {
    if (readyIsLoad) {
      store.load().then((value) => {
        if (!value) {
          backToSearch()
        }
      })
    }
  }, [readyIsLoad])

  useEffect(() => {
    if (readyIsNext) {
      store.setLoading(false)
    }
  }, [readyIsNext])

  if (store.loading) {
    return <SpinnerLoader ariaLabel={'ProfileLayout'} />
  }

  return <Outlet />
})

export const ProfileLayout = observer(() => {
  const entities = useEntitiesContext()
  const store = useMemo(() => new ProfileLayoutPageStore(entities), [])

  return (
    <ProviderProfileLayoutPage value={store}>
      <ProfileLayoutContent />
    </ProviderProfileLayoutPage>
  )
})
